import { FC, ReactNode, useMemo } from 'react';
import { RiskDetailsTagInfo } from '@/components';
import { RiskDetails, RiskType } from '@/models';
import {
  getRiskGroups,
  getRiskTagGroups,
  getRiskScoreValue,
  isFlowLimited,
} from '@/helpers';
import { AmlFlowEnum, SideEnum } from '@/api';
import { AmlHistoryItemData } from '@/redux/models';
import { useTranslation } from 'react-i18next';
import { RISK_GROUPS, RiskKeys } from '@/config';
import bluredDanger from '@/assets/images/blured-danger.svg';
import styles from './RiskDetailsInfoLite.module.sass';

interface RiskDetailsInfoLiteProps {
  item: AmlHistoryItemData;
  hasSignals?: boolean;
}

const RiskDetailsInfoLite: FC<RiskDetailsInfoLiteProps> = (props) => {
  const { item, hasSignals } = props;
  const { t } = useTranslation();
  const groups = getRiskTagGroups(item.signalTags || []);

  const hasSignalValues = useMemo(() => {
    if (!item.signalTags) return false;

    const hasSignals = item.signalTags.length;
    const hasHighRisk = item?.hasHighRisk || false;

    return hasSignals || hasHighRisk;
  }, [item]);

  const signalTitle = useMemo(() => {
    if (hasSignalValues) {
      if (!!item?.tx) {
        return item.direction === SideEnum.DEPOSIT
          ? t('translation.promo_tpl_source_of_funds_str')
          : t('translation.tx_risk_withdrawal_str');
      }

      return t('translation.wallet_connection_title');
    }

    if (!!item?.tx) {
      if (!!item.signalTags) {
        const lowSignalsPresent = [
          ...RISK_GROUPS.suspicious,
          ...RISK_GROUPS.trusted,
        ].some((signal) => item.signalTags?.includes(signal));

        if (!lowSignalsPresent) {
          return '';
        }
      }

      return item.direction === SideEnum.DEPOSIT
        ? t('translation.tx_deposit_connections_empty_msg')
        : t('translation.tx_withdrawal_connections_empty_msg');
    }

    return '';
  }, [item, hasSignalValues, t]);

  const showBluredBlock = useMemo(() => {
    if (!isFlowLimited(item.flow)) {
      return false;
    }
    return item?.hasHighRisk || false;
  }, [item]);

  const sanctions = useMemo(() => {
    if (item.signalTags && item.signalTags.includes(RiskKeys.SANCTIONS)) {
      return [
        {
          label: RiskKeys.SANCTIONS,
          value: getRiskScoreValue(item?.signals?.[RiskKeys.SANCTIONS]) ?? 0,
        } as RiskDetails,
      ];
    }
    return [];
  }, [item]);

  const showDetails = useMemo(() => {
    if (Object.keys(groups).length > 0) {
      return true;
    }

    if (sanctions.length > 0) {
      return true;
    }

    if (showBluredBlock) {
      return true;
    }
    return false;
  }, [sanctions, showBluredBlock, groups]);

  return (
    <>
      {!!signalTitle && (
        <div
          className={`${styles.details__title}${
            !hasSignals ? ` ${styles['details__title--centered']}` : ''
          }`}
        >
          {signalTitle}
        </div>
      )}

      {showDetails && (
        <div className={styles.details__list}>
          {Object.keys(groups).map((category) => (
            <div key={category}>
              <RiskDetailsTagInfo
                category={category}
                risks={groups[category]}
              />
            </div>
          ))}
          {(sanctions.length > 0 || showBluredBlock) && (
            <>
              {sanctions.length > 0 && (
                <RiskDetailsTagInfo category="sanctions" risks={sanctions} />
              )}
              {showBluredBlock && (
                <RiskDetailsTagInfo category="danger" risks={[]}>
                  <img src={bluredDanger} alt="" />
                </RiskDetailsTagInfo>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default RiskDetailsInfoLite;
